import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { FirHoveringDeficitService } from './fir-hovering-deficit.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-fir-hovering-deficit',
    templateUrl: './fir-hovering-deficit.component.html',
    styleUrls: ['./fir-hovering-deficit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirHoveringDeficitComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;
  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  loading = false;
  showData = false;
  pageTitle = 'Hovering Deficit Calculation - Income/(Deficit)';
  columnNames: string[] = [];
  hoveringDeficitsData: any[] = [];
  private isInitialized = false;

  constructor(
    private service: FirHoveringDeficitService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => { this.resetAndTryInit(); })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.resetAndTryInit(); })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) { fp = extractObject(hydrated); }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell, globalParams: mergedGp, filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!(this.GENERAL_CONFIG?.base_url || this.baseURLs?.api)) return;
    this.isInitialized = true;
    this.refresh();
  }

  private params(): any {
    return {
      ho_leid: this.filterParams?.ho_leid,
      ho_cdr_no: this.filterParams?.ho_cdr_no,
      ho_reporting_period: this.filterParams?.ho_reporting_period,
      filing_key: this.filterParams?.filing_key,
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year,
      scenario: this.filterParams?.scenario || this.globalService?.scenario
    };
  }

  refresh(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    this.loading = true;
    this.showData = true;
    this.service.load(baseUrl, this.params()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.parse(res?.jsonObject || []);
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private parse(rows: any[]): void {
    this.columnNames = [];
    this.hoveringDeficitsData = [];
    if (!rows.length) return;
    const headerData = this.safeJson(rows[0]?.HEADER_JSON);
    const basketColumns = headerData
      .filter((h: any) => h?.BASKET && h?.SOURCE)
      .map((h: any) => `${h.BASKET} ${h.SOURCE}`);
    this.columnNames = ['DESCRIPTION', 'TOTAL', ...basketColumns];

    let currentType = '-1';
    let epTaxTotal = 0;
    rows.forEach((r: any, i: number) => {
      if (i === 0) return;
      const epTax = r?.EP_TAX || '';
      if (epTax && epTax !== 'ZZZ' && currentType !== epTax) {
        if (currentType !== '-1') {
          this.hoveringDeficitsData.push({ DESCRIPTION: 'Ending Hovering Deficits', TOTAL: epTaxTotal });
          epTaxTotal = 0;
        }
        this.hoveringDeficitsData.push({ DESCRIPTION: epTax === 'EP' ? 'Earnings and Profits' : 'Taxes' });
      }
      currentType = epTax || currentType;
      if (epTax === 'ZZZ') return;

      const row: any = { DESCRIPTION: r?.DESCRIPTION || `${r?.POOL_MERGE_LEID || ''} - ${r?.POOL_MERGE_TC || ''}` };
      let total = 0;
      headerData.forEach((h: any) => {
        const cat = h?.CATEGORY;
        const col = `${h?.BASKET} ${h?.SOURCE}`;
        if (cat && col) {
          const val = Number(r?.[cat] || 0);
          row[col] = val;
          total += val;
        }
      });
      row.TOTAL = total;
      epTaxTotal += total;
      this.hoveringDeficitsData.push(row);
    });
    if (this.hoveringDeficitsData.length > 0) {
      this.hoveringDeficitsData.push({ DESCRIPTION: 'Ending Hovering Deficits', TOTAL: epTaxTotal });
    }
  }

  private safeJson(v: any): any[] {
    try { return Array.isArray(v) ? v : safeJsonInput<any>(v || '[]', null, 'fir-hovering-deficit.parse1'); } catch { return []; }
  }
}
